<template>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useHead } from 'unhead';

interface SocialMeta {
  socialNetwork: string;
  title: string;
  description: string;
  image: {
    data: {
      attributes: {
        url: string;
      };
    };
  };
}

interface SeoData {
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  metaRobots: string;
  metaImage: {
    data: {
      attributes: {
        url: string;
      };
    };
  };
  metaSocial: SocialMeta[];
}

export default defineComponent({
  name: 'SeoComponent',
  props: {
    seoData: {
      type: Object as () => SeoData,
      required: true,
    },
  },
  setup(props) {
    // Safely get API URL for both client and server environments
    const homeurl: string = typeof import.meta !== 'undefined' ? import.meta.env.VITE_API_URL : '';
    const { metaTitle, metaDescription, keywords, metaRobots, metaImage, metaSocial } = props.seoData;
    
    // Hard-code a fallback domain for URLs when in SSR
    const domain = 'https://rain-digital.ca';
    
    // For SSR, we need to force a value here since window isn't available
    const currentUrl = ref(domain);
    
    // Only update URL on client-side after mount
    onMounted(() => {
      currentUrl.value = window.location.href;
    });
    
    // Ensure image URL is absolute
    const imageUrl = metaImage?.data?.attributes?.url || '';
    const absoluteImageUrl = imageUrl.startsWith('http') 
      ? imageUrl 
      : (homeurl + imageUrl);
    
    // Apply meta tags immediately
    useHead({
      title: metaTitle,
      meta: [
        { name: 'description', content: metaDescription },
        { name: 'keywords', content: keywords },
        { name: 'robots', content: metaRobots },
        
        // Basic OpenGraph tags with additional properties for Facebook
        { property: 'og:title', content: metaTitle },
        { property: 'og:description', content: metaDescription },
        { property: 'og:image', content: absoluteImageUrl },
        { property: 'og:image:secure_url', content: absoluteImageUrl },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:type', content: 'image/png' },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: currentUrl.value },
        
        // Twitter card tags
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: metaTitle },
        { name: 'twitter:description', content: metaDescription },
        { name: 'twitter:image', content: absoluteImageUrl },
        
        // Additional social meta tags
        ...(metaSocial ? metaSocial.map(social => {
          const socialImageUrl = social.image?.data?.attributes?.url || '';
          const absoluteSocialImageUrl = socialImageUrl.startsWith('http') 
            ? socialImageUrl 
            : (homeurl + socialImageUrl);
            
          return [
            {
              property: `og:${social.socialNetwork.toLowerCase()}:title`,
              content: social.title,
            },
            {
              property: `og:${social.socialNetwork.toLowerCase()}:description`,
              content: social.description,
            },
            {
              property: `og:${social.socialNetwork.toLowerCase()}:image`,
              content: absoluteSocialImageUrl,
            }
          ];
        }).flat() : []),
      ],
      // Force these to be at the beginning of head for better crawler detection
      tagPriority: 1
    });

    return {
      homeurl
    };
  },
});
</script>
